import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Mail, Phone, MapPin, Send, CheckCircle, Loader, Paperclip, X, FileText, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { studioInfo } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_FILES = 5;
const ALLOWED_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'doc', 'docx'];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [files, setFiles] = useState([]); // {file, uploading, uploaded, id, error}
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateFile = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTS.includes(ext)) {
      return `Formato non supportato: ${file.name}. Ammessi: ${ALLOWED_EXTS.join(', ')}`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File troppo grande: ${file.name} (max 10 MB)`;
    }
    return null;
  };

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    setErrorMsg('');

    if (files.length + selectedFiles.length > MAX_FILES) {
      setErrorMsg(`Puoi allegare al massimo ${MAX_FILES} file.`);
      return;
    }

    for (const file of selectedFiles) {
      const validationError = validateFile(file);
      if (validationError) {
        setErrorMsg(validationError);
        continue;
      }

      const fileObj = {
        file,
        name: file.name,
        size: file.size,
        uploading: true,
        uploaded: false,
        id: null,
        error: null,
        tempId: `${Date.now()}-${Math.random()}`
      };
      setFiles(prev => [...prev, fileObj]);

      // Upload
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      try {
        const res = await axios.post(`${API}/upload`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setFiles(prev => prev.map(f =>
          f.tempId === fileObj.tempId
            ? { ...f, uploading: false, uploaded: true, id: res.data.id }
            : f
        ));
      } catch (err) {
        const msg = err.response?.data?.detail || 'Errore upload';
        setFiles(prev => prev.map(f =>
          f.tempId === fileObj.tempId ? { ...f, uploading: false, error: msg } : f
        ));
      }
    }

    // Reset input to allow re-selecting the same file
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (tempId) => {
    setFiles(prev => prev.filter(f => f.tempId !== tempId));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (name) => {
    const ext = name.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return ImageIcon;
    return FileText;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    // Ensure no files are still uploading
    if (files.some(f => f.uploading)) {
      setErrorMsg('Attendi il completamento dell\'upload dei file.');
      return;
    }

    setIsSubmitting(true);

    const uploadedIds = files.filter(f => f.uploaded && f.id).map(f => f.id).join(',');

    const submitData = new FormData();
    submitData.append('name', formData.name);
    submitData.append('email', formData.email);
    submitData.append('phone', formData.phone || '');
    submitData.append('subject', formData.subject);
    submitData.append('message', formData.message);
    if (uploadedIds) submitData.append('attachment_ids', uploadedIds);

    try {
      await axios.post(`${API}/contact`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setIsSuccess(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setFiles([]);
      setTimeout(() => setIsSuccess(false), 6000);
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Errore invio richiesta. Riprova.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0A1628] min-h-screen pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full text-[#D4AF37] text-sm font-semibold mb-4"
          >
            Contattaci
          </motion.span>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Parliamo del Tuo <span className="text-[#D4AF37]">Progetto</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Siamo a tua disposizione per consulenze, preventivi e informazioni sui nostri servizi tecnici.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-2 space-y-6"
          >
            <div className="bg-gradient-to-br from-[#1A2B47] to-[#0A1628] rounded-2xl p-8 border border-white/5 shadow-xl">
              <h2 className="text-2xl font-bold text-white mb-6">Informazioni di Contatto</h2>

              <div className="space-y-6">
                <motion.a
                  href={`mailto:${studioInfo.email}`}
                  whileHover={{ x: 5 }}
                  className="flex items-start space-x-4 group"
                >
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4AF37] transition-colors duration-300">
                    <Mail className="w-6 h-6 text-[#D4AF37] group-hover:text-[#0A1628] transition-colors duration-300" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-500 mb-1">Email Principale</p>
                    <p className="text-white font-medium break-all text-sm sm:text-base">{studioInfo.email}</p>
                  </div>
                </motion.a>

                <motion.a
                  href={`mailto:${studioInfo.emailPec}`}
                  whileHover={{ x: 5 }}
                  className="flex items-start space-x-4 group"
                >
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4AF37] transition-colors duration-300">
                    <Mail className="w-6 h-6 text-[#D4AF37] group-hover:text-[#0A1628] transition-colors duration-300" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-500 mb-1">PEC</p>
                    <p className="text-white font-medium break-all text-sm sm:text-base">{studioInfo.emailPec}</p>
                  </div>
                </motion.a>

                <motion.a
                  href={`tel:${studioInfo.phone}`}
                  whileHover={{ x: 5 }}
                  className="flex items-start space-x-4 group"
                >
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4AF37] transition-colors duration-300">
                    <Phone className="w-6 h-6 text-[#D4AF37] group-hover:text-[#0A1628] transition-colors duration-300" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Telefono</p>
                    <p className="text-white font-medium">{studioInfo.phone}</p>
                  </div>
                </motion.a>

                <motion.div whileHover={{ x: 5 }} className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Indirizzo</p>
                    <p className="text-white font-medium">{studioInfo.address}</p>
                  </div>
                </motion.div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#D4AF37] to-[#C5A028] rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold text-[#0A1628] mb-4">Orari di Lavoro</h3>
              <div className="space-y-2 text-[#0A1628]/80">
                <p className="flex justify-between">
                  <span className="font-medium">Lunedì - Venerdì:</span>
                  <span>8:00 - 13:00 / 15:00 - 18:00</span>
                </p>
                <p className="flex justify-between">
                  <span className="font-medium">Sabato:</span>
                  <span>9:00 - 13:00</span>
                </p>
                <p className="flex justify-between">
                  <span className="font-medium">Domenica:</span>
                  <span>Chiuso</span>
                </p>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="lg:col-span-3"
          >
            <div className="bg-gradient-to-br from-[#1A2B47] to-[#0A1628] rounded-2xl p-8 border border-white/5 shadow-xl">
              <h2 className="text-2xl font-bold text-white mb-6">Invia un Messaggio</h2>

              {isSuccess && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center space-x-3"
                  data-testid="contact-success-msg"
                >
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <p className="text-green-400">Messaggio inviato con successo! Ti risponderemo al più presto.</p>
                </motion.div>
              )}

              {errorMsg && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center space-x-3"
                  data-testid="contact-error-msg"
                >
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <p className="text-red-400">{errorMsg}</p>
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="contact-name-input"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-300"
                      placeholder="Mario Rossi"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="contact-email-input"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-300"
                      placeholder="mario.rossi@email.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-400 mb-2">
                      Telefono
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="contact-phone-input"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-300"
                      placeholder="+39 123 456 7890"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-400 mb-2">
                      Oggetto *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      data-testid="contact-subject-input"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-300"
                      placeholder="Richiesta preventivo"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-400 mb-2">
                    Messaggio *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    data-testid="contact-message-input"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-300 resize-none"
                    placeholder="Descrivi il tuo progetto o richiesta..."
                  />
                </div>

                {/* File Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Allegati (opzionale)
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Allega foto, planimetrie o documenti (max {MAX_FILES} file, 10 MB ciascuno). Formati: JPG, PNG, PDF, DOC.
                  </p>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    multiple
                    accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx"
                    className="hidden"
                    data-testid="contact-file-input"
                  />

                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={files.length >= MAX_FILES}
                    data-testid="contact-file-select-btn"
                    className="w-full px-4 py-4 bg-white/5 border-2 border-dashed border-white/20 hover:border-[#D4AF37]/50 hover:bg-white/[0.07] rounded-xl text-gray-400 hover:text-[#D4AF37] transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Paperclip className="w-5 h-5" />
                    <span className="font-medium">
                      {files.length === 0 ? 'Clicca per allegare file' : `Aggiungi altri file (${files.length}/${MAX_FILES})`}
                    </span>
                  </button>

                  {/* File List */}
                  <AnimatePresence>
                    {files.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 space-y-2"
                      >
                        {files.map((f) => {
                          const Icon = getFileIcon(f.name);
                          return (
                            <motion.div
                              key={f.tempId}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 20 }}
                              className={`flex items-center space-x-3 p-3 rounded-xl border ${
                                f.error
                                  ? 'bg-red-500/10 border-red-500/30'
                                  : f.uploaded
                                  ? 'bg-green-500/5 border-green-500/20'
                                  : 'bg-white/5 border-white/10'
                              }`}
                              data-testid={`contact-file-item`}
                            >
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                f.error ? 'bg-red-500/20' : f.uploaded ? 'bg-green-500/20' : 'bg-[#D4AF37]/10'
                              }`}>
                                {f.uploading ? (
                                  <Loader className="w-5 h-5 text-[#D4AF37] animate-spin" />
                                ) : f.uploaded ? (
                                  <CheckCircle className="w-5 h-5 text-green-400" />
                                ) : f.error ? (
                                  <AlertCircle className="w-5 h-5 text-red-400" />
                                ) : (
                                  <Icon className="w-5 h-5 text-[#D4AF37]" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-white text-sm font-medium truncate">{f.name}</p>
                                <p className="text-xs text-gray-500">
                                  {formatSize(f.size)}
                                  {f.uploading && ' • Caricamento...'}
                                  {f.uploaded && ' • Caricato'}
                                  {f.error && ` • ${f.error}`}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFile(f.tempId)}
                                className="w-8 h-8 rounded-lg bg-white/5 hover:bg-red-500/20 text-gray-400 hover:text-red-400 flex items-center justify-center transition-colors duration-200 flex-shrink-0"
                                data-testid="contact-file-remove-btn"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </motion.div>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting || files.some(f => f.uploading)}
                  whileHover={{ scale: (isSubmitting || files.some(f => f.uploading)) ? 1 : 1.02 }}
                  whileTap={{ scale: (isSubmitting || files.some(f => f.uploading)) ? 1 : 0.98 }}
                  data-testid="contact-submit-btn"
                  className="w-full px-8 py-4 bg-gradient-to-r from-[#D4AF37] to-[#C5A028] text-[#0A1628] rounded-xl font-semibold text-lg shadow-lg hover:shadow-2xl hover:shadow-[#D4AF37]/20 transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>Invio in corso...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Invia Messaggio</span>
                    </>
                  )}
                </motion.button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
